import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Sparkles, Star, Zap, Heart, Moon } from "lucide-react";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { dbHelpers } from "@/lib/database";
import { getRandomActivity, getRandomFact } from "@/data/activities";

const HomePage = () => {
  const [todayActivity, setTodayActivity] = useState(null);
  const [isUnlocked, setIsUnlocked] = useState(false);



  const checkDailyActivity = useCallback(async () => {
    // Check if  const loadDailyActivity = useCallback(async () => {
    try {
      // Use offline storage
      const activity = await dbHelpers.getTodayActivity();
      if (activity) {
        setTodayActivity(activity);
        setIsUnlocked(true);
      }
    } catch (error) {
      console.error('Failed to load activity:', error);
    }
  }, []);

  const triggerShakeEasterEgg = useCallback(async () => { // Made async
    if (isUnlocked) return; // Added check

    const activity = getRandomActivity();
    setTodayActivity(activity);
    setIsUnlocked(true);

    // Save unlocked activity
    await dbHelpers.unlockDailyActivity(activity);

    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#FF8FA3', '#FFD93D', '#6BCBFF', '#D4A5FF', '#A5E6A5']
    });

    const fact = getRandomFact();
    toast.success(`✨ Fun Fact: ${fact}`);
  }, [isUnlocked]);

  const setupShakeDetector = useCallback(() => {
    let lastTime = 0;
    let lastX = 0, lastY = 0, lastZ = 0;

    const handleMotion = (event) => {
      const current = event.accelerationIncludingGravity;
      if (!current) return;

      const currentTime = Date.now();

      if ((currentTime - lastTime) > 100) {
        const deltaTime = (currentTime - lastTime) / 1000;
        const deltaX = Math.abs(current.x - lastX);
        const deltaY = Math.abs(current.y - lastY);
        const deltaZ = Math.abs(current.z - lastZ);

        const speed = (deltaX + deltaY + deltaZ) / deltaTime;

        if (speed > 15) {
          triggerShakeEasterEgg();
        }

        lastTime = currentTime;
        lastX = current.x;
        lastY = current.y;
        lastZ = current.z;
      }
    };

    if (window.DeviceMotionEvent) {
      window.addEventListener('devicemotion', handleMotion);
    }

    return () => {
      window.removeEventListener('devicemotion', handleMotion);
    };
  }, [triggerShakeEasterEgg]);

  useEffect(() => {
    checkDailyActivity();
    setupShakeDetector();
  }, [checkDailyActivity, setupShakeDetector]);



  const handleBoredButtonClick = async () => {
    if (isUnlocked) {
      toast.info("You already unlocked today's activity! Come back tomorrow for more magic!");
      return;
    }

    // Get random activity
    const activity = getRandomActivity();
    setTodayActivity(activity);
    setIsUnlocked(true);

    // Confetti celebration
    confetti({
      particleCount: 150,
      spread: 100,
      origin: { y: 0.5 },
      colors: ['#FF8FA3', '#FFD93D', '#6BCBFF', '#D4A5FF']
    });

    // Save to database
    try {
      await dbHelpers.unlockDailyActivity(activity);
      toast.success("🎉 Magic unlocked! Here's your activity!");
    } catch (error) {
      console.error('Failed to save activity:', error);
    }
  };


  return (
    <div className="min-h-screen px-6 py-8 md:px-12">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-12"
      >
        <h1 className="font-fredoka text-5xl md:text-7xl font-bold text-foreground mb-4 tracking-tight">
          Welcome to
          <span className="block text-primary mt-2">SparkleWorld ✨</span>
        </h1>
        <p className="font-nunito text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
          Your magical place for games, crafts, stories, and endless fun!
        </p>
      </motion.div>

      {/* I'm Bored Button */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2 }}
        className="flex flex-col items-center justify-center mb-16"
      >
        <div className="relative">
          {/* Floating decorations */}
          <motion.div
            animate={{ y: [-10, 10, -10] }}
            transition={{ duration: 3, repeat: Infinity }}
            className="absolute -top-8 -left-8"
          >
            <Star className="w-8 h-8 text-secondary fill-secondary" />
          </motion.div>
          <motion.div
            animate={{ y: [10, -10, 10] }}
            transition={{ duration: 2.5, repeat: Infinity }}
            className="absolute -top-6 -right-6"
          >
            <Sparkles className="w-10 h-10 text-accent" />
          </motion.div>

          {/* Main Button */}
          <motion.button
            data-testid="bored-button"
            onClick={handleBoredButtonClick}
            disabled={isUnlocked}
            whileHover={{ scale: isUnlocked ? 1 : 1.05, rotate: isUnlocked ? 0 : -2 }}
            whileTap={{ scale: isUnlocked ? 1 : 0.95 }}
            className={`
              relative px-12 py-8 rounded-full text-white font-fredoka text-3xl md:text-4xl font-bold
              shadow-hard-xl transition-all
              ${isUnlocked
                ? 'bg-muted-foreground cursor-not-allowed'
                : 'bg-primary hover:shadow-2xl hover:-translate-y-1'
              }
            `}
          >
            {isUnlocked ? "✅ Already Unlocked!" : "I'm Bored! 🎪"}
          </motion.button>

        </div>
      </motion.div>

      {/* Today's Activity Card */}
      {todayActivity && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="activity-card"
          className="max-w-2xl mx-auto bg-white border-2 border-border rounded-3xl shadow-hard-lg p-8"
        >
          <div className="text-center mb-6">
            <span className="text-6xl mb-4 block">{todayActivity.icon}</span>
            <h2 className="font-fredoka text-3xl font-bold text-foreground mb-2">
              {todayActivity.title}
            </h2>
            <p className="font-nunito text-lg text-muted-foreground">
              {todayActivity.description}
            </p>
          </div>

          <div className="space-y-4">
            {todayActivity.duration && (
              <div className="flex items-center gap-2 text-foreground">
                <Zap className="w-5 h-5 text-secondary" />
                <span className="font-nunito">Duration: {todayActivity.duration}</span>
              </div>
            )}

            {todayActivity.materials && (
              <div>
                <h3 className="font-fredoka text-xl font-semibold text-foreground mb-2">
                  What you need:
                </h3>
                <ul className="space-y-1 pl-6">
                  {todayActivity.materials.map((item, index) => (
                    <li key={index} className="font-nunito text-muted-foreground list-disc">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {todayActivity.recipe && (
              <div>
                <h3 className="font-fredoka text-xl font-semibold text-foreground mb-2">
                  Recipe:
                </h3>
                <ol className="space-y-1 pl-6">
                  {todayActivity.recipe.map((step, index) => (
                    <li key={index} className="font-nunito text-muted-foreground list-decimal">
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* Quick Stats */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto"
      >
        {[
          { icon: Star, label: "Games", count: "3", color: "text-accent" },
          { icon: Heart, label: "Crafts", count: "9", color: "text-secondary" },
          { icon: Sparkles, label: "Activities", count: "8", color: "text-primary" },
          { icon: Moon, label: "Diary", count: "∞", color: "text-magic" }
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 + index * 0.1 }}
            data-testid={`stat-${stat.label.toLowerCase()}`}
            className="bg-white border-2 border-border rounded-2xl shadow-hard p-6 text-center hover:shadow-hard-lg transition-all"
          >
            <stat.icon className={`w-8 h-8 ${stat.color} mx-auto mb-2`} />
            <p className="font-fredoka text-2xl font-bold text-foreground">{stat.count}</p>
            <p className="font-nunito text-sm text-muted-foreground">{stat.label}</p>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

export default HomePage;
